import { cn } from "@sagot/ui";
import { useRef, type KeyboardEvent } from "react";

export interface StageTab {
  value: string;
  label: string;
  count: number | null;
}

interface StageTabsProps {
  label: string;
  tabs: readonly StageTab[];
  value: string;
  onChange: (value: string) => void;
  controls: string;
}

/** Mockup `.segmented` stage filter with counts; roving tablist (← / → / Home / End). */
export function StageTabs({ label, tabs, value, onChange, controls }: StageTabsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusAt = (index: number) => {
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    onChange(tab.value);
    refs.current[(index + tabs.length) % tabs.length]?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const moves: Record<string, number> = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    focusAt(next);
  };
  return (
    <div className="max-w-full overflow-x-auto">
      <div className="segmented" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const selected = tab.value === value;
          return (
            <button
              key={tab.value}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={controls}
              tabIndex={selected ? 0 : -1}
              className={cn("whitespace-nowrap")}
              onClick={() => {
                onChange(tab.value);
              }}
              onKeyDown={(event) => {
                onKeyDown(event, index);
              }}
            >
              {tab.label}
              {tab.count === null ? null : <span className="ml-1 text-fg-subtle">{tab.count}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
